import ConfirmDialog from '@/components/ConfirmDialog';
import ButtonEdge from '@/components/flow/ButtonEdge';
import FlowSidebar from '@/components/flow/FlowSidebar';
import NodeConfigSidebar from '@/components/flow/NodeConfigSidebar';
import ApiNode from '@/components/flow/nodes/ApiNode';
import ConditionNode from '@/components/flow/nodes/ConditionNode';
import DelayNode from '@/components/flow/nodes/DelayNode';
import MessageNode from '@/components/flow/nodes/MessageNode';
import TriggerNode from '@/components/flow/nodes/TriggerNode';
import automationFlowsService from '@/services/automationFlowsService';
import { toastService } from '@/services/toastService';
import { whatsappConfigService } from '@/services/whatsappConfigService';
import {
  addEdge,
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  ChevronLeft,
  Clock,
  GitBranch,
  Globe,
  Loader2,
  MessageSquare,
  Save,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const nodeTypes = {
  triggerNode: TriggerNode,
  messageNode: MessageNode,
  delayNode: DelayNode,
  conditionNode: ConditionNode,
  apiNode: ApiNode,
};

const edgeTypes = {
  buttonEdge: ButtonEdge,
};

const initialNodes = [
  {
    id: 'trigger_1',
    type: 'triggerNode',
    data: { keywords: ['Hello', 'Hi'] },
    position: { x: 250, y: 5 },
  },
  {
    id: 'node_1',
    type: 'messageNode',
    data: { message: 'Welcome to our service! How can I assist you today?' },
    position: { x: 250, y: 150 },
  },
];

const initialEdges = [
  { id: 'e1-2', source: 'trigger_1', target: 'node_1', type: 'buttonEdge' },
];

let id = 0;
const getId = () => `node_${id++}_${Math.random().toString(36).substr(2, 9)}`;

const FlowBuilderInner = () => {
  const { projectId, flowId } = useParams();
  const navigate = useNavigate();
  const reactFlowWrapper = useRef(null);
  const connectingNodeId = useRef(null);
  const menuOpenedAt = useRef(0);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flowName, setFlowName] = useState('New Flow');
  const [flowDescription, setFlowDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [whatsappConfig, setWhatsappConfig] = useState(null);
  const [confirmDeleteNode, setConfirmDeleteNode] = useState({
    isOpen: false,
    nodeId: null,
  });

  const { screenToFlowPosition } = useReactFlow();

  // Mapping from Frontend Node to Backend Node
  const mapNodeToBackend = node => {
    const backendNode = {
      id: node.id,
      position: node.position,
      data: {},
    };

    if (node.type === 'messageNode') {
      const type = node.data.type || 'text';
      if (type === 'buttons') {
        const interactiveData = {
          type: 'button',
          body: {
            text: node.data.message || '',
          },
          action: {
            buttons: (node.data.buttons || []).map(b => ({
              type: 'reply',
              reply: {
                id: b.id,
                title: b.label || '',
              },
            })),
          },
        };

        if (node.data.footer) {
          interactiveData.footer = { text: node.data.footer };
        }

        if (node.data.headerType && node.data.headerType !== 'none') {
          const header = { type: node.data.headerType };
          if (node.data.headerType === 'text') {
            header.text = node.data.headerText || '';
          } else {
            // image, video, document
            header[node.data.headerType] = {
              id: node.data.headerMediaId,
              key: node.data.headerMediaKey,
            };
          }
          interactiveData.header = header;
        }

        backendNode.type = 'send_buttons';
        backendNode.data = {
          type: 'interactive',
          interactive: interactiveData,
          // Preserve for execution service & round-trip
          responseVariable: node.data.responseVariable || '',
        };
      } else if (type === 'list') {
        const interactiveData = {
          type: 'list',
          body: { text: node.data.message || '' },
          action: {
            button: node.data.listButtonText || 'Select Option',
            sections: [
              {
                title: node.data.listTitle || 'Options',
                rows: (node.data.listItems || []).map(item => ({
                  id: item.id,
                  title: item.title,
                  description: item.description || '',
                })),
              },
            ],
          },
        };

        if (node.data.footer) {
          interactiveData.footer = { text: node.data.footer };
        }

        backendNode.type = 'send_list';
        backendNode.data = {
          type: 'interactive',
          interactive: interactiveData,
          // Store original frontend fields for round-trip
          _listItems: node.data.listItems || [],
          _listButtonText: node.data.listButtonText || '',
          _listTitle: node.data.listTitle || '',
          _footer: node.data.footer || '',
          _message: node.data.message || '',
          responseVariable: node.data.responseVariable || '',
        };
      } else if (['image', 'video', 'document'].includes(type)) {
        backendNode.type = `send_${type}`;
        backendNode.data = {
          type: type,
          [type]: {
            id: node.data.mediaId,
            caption: node.data.message || '',
          },
          // Store extra fields for round-trip restore
          _mediaId: node.data.mediaId,
          _mediaKey: node.data.mediaKey,
          _mediaFileName: node.data.mediaFileName,
          _caption: node.data.message || '',
        };
      } else if (type === 'template') {
        backendNode.type = 'send_template';
        backendNode.data = {
          type: 'template',
          templateId: node.data.templateId,
          templateName: node.data.templateName,
          // Store the full template object (header, body, footer, buttons)
          // The WhatsApp API components array is built at execution time
          template: node.data.template || null,
          templateVariables: node.data.templateVariables || null,
          // Store media IDs at the top level for easy access during execution
          headerMediaId: node.data.headerMediaId || null,
          headerMediaKey: node.data.headerMediaKey || null,
        };
      } else {
        backendNode.type = 'send_text';
        backendNode.data = {
          content: node.data.message || '',
        };
      }
    } else if (node.type === 'triggerNode') {
      backendNode.type = 'triggerNode';
      backendNode.data = {
        triggerType: node.data.triggerType || 'keyword',
        keywords: node.data.keywords || [],
        matchType: node.data.matchType || 'payload',
        payloads: node.data.payloads || [],
        templateId: node.data.templateId || null,
        templateName: node.data.templateName || null,
        template: node.data.template || null,
      };
    } else {
      // Keep other types as is for visual persistence
      backendNode.type = node.type;
      backendNode.data = node.data;
    }

    return backendNode;
  };

  // Mapping from Backend Node to Frontend Node
  const mapNodeToFrontend = node => {
    const frontendNode = {
      id: node.id,
      position: node.position,
    };

    if (node.type === 'send_text') {
      frontendNode.type = 'messageNode';
      frontendNode.data = {
        type: 'text',
        message: node.data.content || '',
      };
    } else if (
      node.type === 'send_list' ||
      (node.type === 'send_buttons' &&
        node.data?.type === 'interactive' &&
        node.data?.interactive?.type === 'list')
    ) {
      frontendNode.type = 'messageNode';
      frontendNode.data = {
        type: 'list',
        message: node.data._message || node.data.interactive?.body?.text || '',
        listButtonText: node.data._listButtonText || node.data.interactive?.action?.button || 'Select Option',
        listTitle: node.data._listTitle || node.data.interactive?.action?.sections?.[0]?.title || '',
        footer: node.data._footer || node.data.interactive?.footer?.text || '',
        listItems: node.data._listItems ||
          (node.data.interactive?.action?.sections?.[0]?.rows || []).map(r => ({
            id: r.id,
            title: r.title,
            description: r.description || '',
          })),
        responseVariable: node.data.responseVariable || '',
      };
    } else if (
      ['send_image', 'send_video', 'send_document'].includes(node.type)
    ) {
      const mediaType = node.type.replace('send_', '');
      frontendNode.type = 'messageNode';
      frontendNode.data = {
        type: mediaType,
        mediaId: node.data._mediaId || node.data[mediaType]?.id || '',
        mediaKey: node.data._mediaKey || '',
        mediaFileName: node.data._mediaFileName || '',
        message: node.data._caption || node.data[mediaType]?.caption || '',
      };
    } else if (
      node.type === 'send_buttons' &&
      node.data?.type === 'interactive' &&
      node.data?.interactive?.type === 'button'
    ) {
      const interactive = node.data.interactive;
      frontendNode.type = 'messageNode';
      frontendNode.data = {
        type: 'buttons',
        message: interactive.body?.text || '',
        footer: interactive.footer?.text || '',
        buttons: (interactive.action?.buttons || []).map(b => ({
          id: b.reply?.id || getId(),
          label: b.reply?.title || '',
        })),
        headerType: interactive.header?.type || 'none',
        headerText: interactive.header?.text || '',
        headerMediaId: interactive.header?.[interactive.header?.type]?.id || '',
        headerMediaKey:
          interactive.header?.[interactive.header?.type]?.key || '',
        responseVariable: node.data.responseVariable || '',
      };
    } else if (node.type === 'send_template') {
      // node.data.template is now stored as the raw template object (header, body, footer, buttons)
      frontendNode.type = 'messageNode';
      frontendNode.data = {
        type: 'template',
        templateId: node.data.templateId || null,
        templateName: node.data.templateName || node.data.template?.name,
        template: node.data.template || null,
        templateVariables: node.data.templateVariables || null,
        headerMediaId: node.data.headerMediaId || '',
        headerMediaKey: node.data.headerMediaKey || '',
      };
    } else if (node.type === 'triggerNode') {
      frontendNode.type = 'triggerNode';
      frontendNode.data = {
        triggerType: node.data.triggerType || 'keyword',
        keywords: node.data.keywords || [],
        matchType: node.data.matchType || 'payload',
        payloads: node.data.payloads || [],
        templateId: node.data.templateId || null,
        templateName: node.data.templateName || null,
        template: node.data.template || null,
        headerMediaId: node.data.headerMediaId || null,
        headerMediaKey: node.data.headerMediaKey || null,
      };
    } else {
      frontendNode.type = node.type;
      frontendNode.data = node.data;
    }

    return frontendNode;
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response =
          await whatsappConfigService.getWhatsAppConfig(projectId);
        if (response.success) {
          setWhatsappConfig(response.data.whatsappConfig || response.data);
        }
      } catch (error) {
        console.error('Error fetching WhatsApp config:', error);
      }
    };
    fetchConfig();
  }, [projectId]);

  useEffect(() => {
    if (flowId && flowId !== 'create') {
      const fetchFlow = async () => {
        try {
          setLoading(true);
          const response = await automationFlowsService.getFlowById(
            flowId,
            projectId
          );
          if (response.success) {
            const flow = response.data;
            setFlowName(flow.name);
            setFlowDescription(flow.description || '');
            setIsActive(flow.isActive ?? true);

            const mappedNodes = (flow.nodes || []).map(mapNodeToFrontend);
            setNodes(mappedNodes);
            setEdges(flow.edges || []);

            // Wait for nodes to render then fit view
            setTimeout(() => {
              if (mappedNodes.length > 0) {
                // fitting view will be handled by fitView prop initially,
                // but for loaded data we might want to ensure it works
              }
            }, 100);
          }
        } catch (error) {
          console.error('Error fetching flow:', error);
          toastService.showError('Failed to load flow data');
        } finally {
          setLoading(false);
        }
      };
      fetchFlow();
    }
  }, [flowId, projectId, setNodes, setEdges]);

  const onConnect = useCallback(
    params => {
      setEdges(eds => addEdge({ ...params, type: 'buttonEdge' }, eds));
      connectingNodeId.current = null;
    },
    [setEdges]
  );

  const onConnectStart = useCallback((_, { nodeId, handleId, handleType }) => {
    connectingNodeId.current = { nodeId, handleId, handleType };
  }, []);

  const onConnectEnd = useCallback((event, connectionState) => {
    if (!connectingNodeId.current) return;

    const { isValid, toNode } = connectionState;

    if (!isValid || !toNode) {
      const id = getId();
      const clientX = event.clientX ?? event.changedTouches?.[0]?.clientX;
      const clientY = event.clientY ?? event.changedTouches?.[0]?.clientY;

      menuOpenedAt.current = Date.now();

      setMenu({
        id,
        top: clientY,
        left: clientX,
      });
    } else {
      connectingNodeId.current = null;
    }
  }, []);

  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    if (Date.now() - menuOpenedAt.current < 200) return;
    setSelectedNode(null);
    setMenu(null);
    connectingNodeId.current = null;
  }, []);

  const updateNode = useCallback(
    (nodeId, newData) => {
      setNodes(nds =>
        nds.map(node => {
          if (node.id === nodeId) {
            return { ...node, data: newData };
          }
          return node;
        })
      );
    },
    [setNodes]
  );

  const handleConfigSave = (nodeId, newData) => {
    updateNode(nodeId, newData);
    setSelectedNode(null);
  };

  const deleteNode = useCallback(nodeId => {
    setConfirmDeleteNode({ isOpen: true, nodeId });
  }, []);

  const onConfirmDeleteNode = () => {
    const nodeId = confirmDeleteNode.nodeId;
    if (!nodeId) return;

    setNodes(nds => nds.filter(node => node.id !== nodeId));
    setEdges(eds =>
      eds.filter(edge => edge.source !== nodeId && edge.target !== nodeId)
    );
    setSelectedNode(null);
    setConfirmDeleteNode({ isOpen: false, nodeId: null });
  };

  const handleAddNodeFromMenu = useCallback(
    type => {
      const conn = connectingNodeId.current;
      if (!menu || !conn) return;

      const position = screenToFlowPosition({
        x: menu.left,
        y: menu.top,
      });

      const getInitialData = nodeType => {
        switch (nodeType) {
          case 'triggerNode':
            return { keywords: [] };
          case 'messageNode':
            return { message: 'Write your message here...', type: 'text' };
          case 'delayNode':
            return { duration: '5', unit: 'minutes' };
          case 'conditionNode':
            return { condition: '' };
          default:
            return {};
        }
      };

      const newNode = {
        id: menu.id,
        type,
        position,
        data: getInitialData(type),
      };

      setNodes(nds => nds.concat(newNode));
      setEdges(eds =>
        eds.concat({
          id: `e-${conn.nodeId}-${menu.id}`,
          source: conn.nodeId,
          sourceHandle: conn.handleId,
          target: menu.id,
          type: 'buttonEdge',
        })
      );

      setMenu(null);
      connectingNodeId.current = null;
    },
    [menu, screenToFlowPosition, setNodes, setEdges]
  );

  const handleConnectToExistingNode = useCallback(
    targetId => {
      const conn = connectingNodeId.current;
      if (!conn) return;

      setEdges(eds =>
        eds.concat({
          id: `e-${conn.nodeId}-${targetId}`,
          source: conn.nodeId,
          sourceHandle: conn.handleId,
          target: targetId,
          type: 'buttonEdge',
        })
      );
      setMenu(null);
      connectingNodeId.current = null;
    },
    [setEdges]
  );

  const onDragOver = useCallback(event => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    event => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/reactflow');
      if (typeof type === 'undefined' || !type) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const getInitialData = nodeType => {
        switch (nodeType) {
          case 'triggerNode':
            return { keywords: [] };
          case 'messageNode':
            return { message: 'Write your message here...', type: 'text' };
          case 'delayNode':
            return { duration: '5', unit: 'minutes' };
          case 'conditionNode':
            return { condition: '' };
          default:
            return {};
        }
      };

      const newNode = {
        id: getId(),
        type,
        position,
        data: getInitialData(type),
      };

      setNodes(nds => nds.concat(newNode));
    },
    [screenToFlowPosition, setNodes]
  );

  const onSave = async () => {
    if (!flowName.trim()) {
      toastService.showError('Please enter a flow name');
      return;
    }

    try {
      setSaving(true);

      // Extract trigger configuration
      const triggerNode = nodes.find(n => n.type === 'triggerNode');
      const triggerType = triggerNode?.data?.triggerType || 'keyword';

      let trigger;
      if (triggerType === 'template_response') {
        const template = triggerNode.data.template;
        const buttons = template?.buttons || [];
        const payloads = buttons.map(b => b.text); // Since we use text as payload in our system

        trigger = {
          type: 'template_response',
          config: {
            matchType: 'payload', // Default to payload matching for templates
            payloads: payloads,
            templateId: triggerNode?.data?.templateId || null,
            templateName: triggerNode?.data?.templateName || null,
            template: template, // Include full template for backend reference
            headerMediaId: triggerNode?.data?.headerMediaId || null,
            headerMediaKey: triggerNode?.data?.headerMediaKey || null,
          },
        };
      } else {
        trigger = {
          type: 'message_received',
          config: {
            matchType: 'exact',
            keywords: triggerNode?.data?.keywords || ['hello'],
          },
        };
      }

      const flowData = {
        name: flowName,
        description: flowDescription,
        projectId,
        isActive,
        trigger,
        nodes: nodes.map(mapNodeToBackend),
        edges,
      };

      let response;
      if (flowId === 'create') {
        response = await automationFlowsService.createFlow(flowData);
      } else {
        response = await automationFlowsService.updateFlow(flowId, flowData);
      }

      if (response.success) {
        toastService.showSuccess(
          `Flow ${flowId === 'create' ? 'created' : 'updated'} successfully`
        );
        navigate(`/projects/${projectId}/flows`);
      }
    } catch (error) {
      console.error('Error saving flow:', error);
      toastService.showError('Failed to save flow');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="h-12 w-12 text-primary-500 animate-spin mb-4" />
        <p className="text-gray-600 font-medium text-lg">
          Loading builder and flow configuration...
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Builder Header */}
      <div className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 flex-shrink-0 z-10">
        <div className="flex items-center gap-4 flex-1">
          <button
            onClick={() => navigate(`/projects/${projectId}/flows`)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div className="flex flex-col">
            <input
              type="text"
              value={flowName}
              onChange={e => setFlowName(e.target.value)}
              className="text-lg font-semibold text-gray-900 bg-transparent border-none focus:ring-0 p-0 hover:bg-gray-50 rounded px-2 -ml-2"
              placeholder="Flow Name"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 mr-4 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
            <span className="text-xs font-medium text-gray-600">Active</span>
            <button
              onClick={() => setIsActive(!isActive)}
              className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none ${
                isActive ? 'bg-primary-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  isActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <button
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors shadow-sm disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Flow
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Builder Canvas */}
        <div className="flex-1 relative overflow-hidden" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectStart={onConnectStart}
            onConnectEnd={onConnectEnd}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            className="bg-slate-50"
          >
            <Background color="#cbd5e1" gap={20} />
            <Controls />
            <MiniMap
              nodeStrokeColor={() => '#1a192b'}
              nodeColor={() => '#fff'}
            />
          </ReactFlow>

          {menu && (
            <div
              className="fixed z-[1000] animate-in fade-in zoom-in duration-200"
              style={{
                top: menu.top,
                left: menu.left,
              }}
            >
              <div className="bg-white/90 backdrop-blur-xl border border-white/40 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.2)] p-3 w-64 ring-1 ring-black/5 overflow-hidden">
                <div className="mb-3 px-3">
                  <p className="text-[10px] font-black text-primary-500 uppercase tracking-[0.2em]">
                    Connect to...
                  </p>
                </div>

                <div className="space-y-1">
                  <button
                    onClick={() => handleAddNodeFromMenu('messageNode')}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-primary-50 rounded-xl transition-all group border border-transparent hover:border-primary-100"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-500 text-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                        <MessageSquare size={16} />
                      </div>
                      <span className="font-bold">Message</span>
                    </div>
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>

                  <button
                    onClick={() => handleAddNodeFromMenu('delayNode')}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-amber-50 rounded-xl transition-all group border border-transparent hover:border-amber-100"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-400 text-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                        <Clock size={16} />
                      </div>
                      <span className="font-bold">Delay</span>
                    </div>
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>

                  <button
                    onClick={() => handleAddNodeFromMenu('conditionNode')}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 rounded-xl transition-all group border border-transparent hover:border-indigo-100"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-500 text-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                        <GitBranch size={16} />
                      </div>
                      <span className="font-bold">Condition</span>
                    </div>
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>

                  <button
                    onClick={() => handleAddNodeFromMenu('apiNode')}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-emerald-50 rounded-xl transition-all group border border-transparent hover:border-emerald-100"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-500 text-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                        <Globe size={16} />
                      </div>
                      <span className="font-bold">API Request</span>
                    </div>
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100/60">
                  <div className="px-3 mb-2">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                      Link to Existing...
                    </p>
                  </div>
                  <div className="max-h-40 overflow-y-auto pr-1 space-y-1 custom-scrollbar">
                    {nodes
                      .filter(n => n.id !== connectingNodeId.current?.nodeId)
                      .map(node => (
                        <button
                          key={node.id}
                          onClick={() => handleConnectToExistingNode(node.id)}
                          className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-600 hover:bg-gray-50 hover:text-primary-600 rounded-lg transition-all group border border-transparent hover:border-gray-100"
                        >
                          <div
                            className={`w-2 h-2 rounded-full ${node.id === '1' ? 'bg-primary-500' : 'bg-gray-300'} group-hover:scale-125 transition-transform`}
                          />
                          <span className="truncate font-medium">
                            {node.id === '1'
                              ? 'Start / Trigger'
                              : node.data?.message ||
                                node.data?.label ||
                                `${node.type} (${node.id.split('_')[1] || node.id})`}
                          </span>
                        </button>
                      ))}
                    {nodes.filter(
                      n => n.id !== connectingNodeId.current?.nodeId
                    ).length === 0 && (
                      <p className="text-[10px] text-gray-400 italic px-3 py-2 text-center">
                        No other nodes available
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebars */}
        <div className="flex flex-col border-l border-gray-200">
          <FlowSidebar />
        </div>

        <NodeConfigSidebar
          selectedNode={selectedNode}
          onUpdate={updateNode}
          onSave={handleConfigSave}
          onClose={() => setSelectedNode(null)}
          onDelete={deleteNode}
          whatsappConfig={whatsappConfig}
        />
      </div>

      <ConfirmDialog
        isOpen={confirmDeleteNode.isOpen}
        title="Delete Node"
        message="Are you sure you want to delete this node? This will also remove all connected lines."
        confirmText="Delete Node"
        confirmVariant="danger"
        onConfirm={onConfirmDeleteNode}
        onCancel={() => setConfirmDeleteNode({ isOpen: false, nodeId: null })}
      />
    </div>
  );
};

const FlowBuilder = () => (
  <ReactFlowProvider>
    <FlowBuilderInner />
  </ReactFlowProvider>
);

export default FlowBuilder;
